import React, { useState, useEffect, useMemo, useRef } from 'react';
import { LoadingIcon, FullScreenLoadingCover } from '@prosolve/common-components-ui'
import Chevron from './Chevron';
import QuestChevron from './QuestChevron';
import MainQuestionAnswerPanel from './MainQuestionAnswerPanel';
import ProgressBar from './ProgressBar';
import { FaTimes } from 'react-icons/fa';
import useFetch from '../../hooks/useFetch';
import useIsMobileView from "../../hooks/useIsMobileView";
import TeamSelector from './TeamSelector';
import SoefiaBackground from '../../images/soefia-assessment/bkg-metal.png';
import Timer from './Timer';
import { regexFromString } from '../../utils'
import { parseNumberOfStudents, isStudentPlacementComplete } from './studentPlacement'
// import SoefiaBackgroundFrame from '../../images/soefia-assessment/ui_frame-teacher-default.png';
// import SoefiaBackgroundFrameCombined from '../../images/soefia-assessment/bkg_frameCombined.png';

// import modelData from './testData/StudentStarforcePostProgramSurvey.json';

// These assessments only make sense with a mod (assessmentsMods doc selecting the questions).
// Opening them without a mod, or with one that doesn't resolve, shows "Survey not found".
const MOD_REQUIRED_ASSESSMENTS = ['PulseCheck', 'TeacherPulseCheck']

// requiredStudentTotal: when set (TeacherPulseCheck opened with numberOfStudents), multitext
// questions with numeric options must total exactly that number to count as answered
const hasQuestionBeenAnswered = (questionObj, answers, currentQuestionStep, requiredStudentTotal) => {
  const {
    id: currentQuestionId,
    type: currentQuestionType,
    validationRegex,
    soefiaConfig: {
      challengeUrl,
      challengeImage,
    } = {}
  } = questionObj;

  const isSoefiaQ = currentQuestionType === 'soefia'

  const currentAnswerObj = answers[currentQuestionId] || {};
  const {
    answerText = '',
    answerArray = []
  } = currentAnswerObj;

  let currentQuestionHasBeenAnswered;
  if (currentQuestionType === 'multitext') {
    const placementComplete = requiredStudentTotal !== undefined
      ? isStudentPlacementComplete(questionObj, currentAnswerObj, requiredStudentTotal)
      : undefined;
    if (placementComplete !== undefined) {
      currentQuestionHasBeenAnswered = placementComplete;
    } else {
      let answerCount = 0;
      answerArray.forEach(({ answerText }) => {
        if (answerText) {
          answerCount++;
        }
      });
      currentQuestionHasBeenAnswered = answerCount > 0; // (answerCount === currentQuestionAnswerOptions.length);
    }
  } else if (isSoefiaQ) { // && currentQuestionStep === totalQuestionSteps ) { 
    // for now (will need to change later) mark question answered when we are on the final soefia question step
    // currentQuestionHasBeenAnswered = true;
    if (!challengeUrl && !challengeImage && currentQuestionStep === 0) {
      currentQuestionHasBeenAnswered = true;
    } else {
      const {
        soefiaAnswer = {}
      } = currentAnswerObj

      // Find if file for prompt has been added

      // prompt index to check will always be 1 less then the current step (unless we have a challengeUrl in which we require an answer on intro)
      const promptCheckIndex = (challengeUrl || challengeImage) ? currentQuestionStep : currentQuestionStep - 1;

      const promptCheckKey = Object.keys(soefiaAnswer)[promptCheckIndex]

      const promptCheckObj = soefiaAnswer[promptCheckKey] || {}

      const { files = [] } = promptCheckObj;

      if (files.length > 0) {
        currentQuestionHasBeenAnswered = 1;
      }
    }
  } else if (currentQuestionType === 'text' && Boolean(validationRegex)) {
    currentQuestionHasBeenAnswered = answerText && regexFromString(validationRegex)?.test(answerText);
  } else {
    currentQuestionHasBeenAnswered = answerText || (answerArray && answerArray.length > 0);
  }

  return Boolean(currentQuestionHasBeenAnswered)
}


export default ({
  assessmentName = '',
  mod = '',
  classId,
  episodeId,
  eventId,
  userId = '',
  postSurveyQFCAction,
  teamColor: initTeamColor = '',
  teamName,
  assignmentId,
  assignmentTargetType,
  groupId,
  sessionId,
  numberOfStudents,
  hasCloseButton,
  isPreview,
  isPractice,
  previewData = {},
  onClose = () => {},
  onSuccess = () => {},
  onAssessmentDataChanged,
  inFollowUp,
  hasFollowUp
}) => {

  const hasUnsavedChanges = useRef(false)

  window.addEventListener("beforeunload", (event) => {
  if (!hasUnsavedChanges.current) return;

  // Required for the prompt to appear in most browsers
  event.preventDefault();

  // Chrome still expects returnValue to be set (string content is ignored)
  event.returnValue = "";
});

  const isMobileView = useIsMobileView();

  const fetch = useFetch();

  const initAssessmentData = isPreview ? previewData : {};

  const initShowIntroPage = isPreview ? (Boolean(previewData.intro) && !previewData.intro.hideIntro) : undefined;

  const [assessmentData, setAssessmentData] = useState(initAssessmentData)
  const [showIntroPage, setShowIntroPage] = useState(initShowIntroPage);
  const [showTeamSelectorPage, setShowTeamSelectorPage] = useState();
  const [loading, setLoading] = useState(isPreview ? false : true)

  const [assessmentNotFound, setAssessmentNotFound] = useState(false)
  const [assessmentDisabled, setAssessmentDisabled] = useState(false)

  const [teamColor, setTeamColor] = useState(initTeamColor)

  useEffect(() => {
    setTeamColor(initTeamColor)
  }, [initTeamColor])

  useEffect(() => {
    if (onAssessmentDataChanged && assessmentData) {
      onAssessmentDataChanged(assessmentData)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentData])

  const {
    _id: assessmentId,
    backgroundImage,
    intro,
    modName,
    questions = []
  } = assessmentData;

  const introObj = intro || {};

  const {
    title = '',
    subTitle = '',
    buttonText = 'Get Started',
    backgroundImageOrientation = '',
    teamSelector = '',
    timeLimit = '',
    largeFonts,
    extraLargeFonts,
    reverseOptions,
    hideIntro,
    isVerticalScrollMode: isVerticalScrollModeRaw,
    autoPlayAudio
  } = introObj;

  const hasIntro = Boolean(intro) && !hideIntro && !inFollowUp;
  const hasTeamSelector = Boolean(teamSelector)

  const handleAssessmentNotFound = () => {
    setAssessmentNotFound(true)
    setLoading(false)
    onClose()
  }

  const getAssessmentData = async () => {
    try {
      setLoading(true)

      const requiresMod = MOD_REQUIRED_ASSESSMENTS.includes(assessmentName)

      if (requiresMod && !mod) {
        handleAssessmentNotFound()
        return
      }

      const response = await fetch(`assessment?name=${assessmentName}${mod ? `&mod=${mod}` : ''}`, { method: 'GET' })
      const jsonData = await response.json();

      const data = jsonData || {};

      // The API only adds modName when the mod resolved and was applied. For mod-required
      // assessments, a response without it means the mod was missing, malformed, nonexistent,
      // or belongs to a different assessment, so treat it the same as an unknown assessment.
      if (requiresMod && data.modName === undefined) {
        handleAssessmentNotFound()
        return
      }

      // Only set data if we have not explicitely disabled the assessment
      if (!data.disabled) {
        setAssessmentData(data)

        setShowIntroPage(Boolean(data.intro) && !data.intro.hideIntro && !inFollowUp)
      } else {
        setAssessmentDisabled(true)
      }

      setLoading(false)
    } catch (e) {
      console.log(e)
      handleAssessmentNotFound()
    }
  }

  useEffect(() => {
    if (!isPreview) {
      getAssessmentData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreview])

  const categoryInfoMap = useMemo(() => {
    const catInfoMap = {};
    questions.forEach(({id, category}) => {
      if (category) {
        if (!catInfoMap[category]) {
          catInfoMap[category] = [];
        }
        catInfoMap[category].push(id)
      }
    });
    return catInfoMap;
  }, [questions]);

  const isSoefiaAssessment = useMemo(() => {
    let hasSoefiaType = false;
    questions.forEach(({ type }) => {
      if (type === 'soefia') {
        hasSoefiaType = true;
      }
    });
    return hasSoefiaType;
  }, [questions]);

  // Extra check here is to ensure that Soefia assessments cannot be accidently set into vertical scroll mode
  const isVerticalScrollMode = isVerticalScrollModeRaw && !isSoefiaAssessment

  const [isRecordingInProgress, setIsRecordingInProgress] = useState(false)

  const isRecordingInProgressRef = useRef(false)
  useEffect(() => {
    if (isRecordingInProgress) {
      hasUnsavedChanges.current = true;
    }
    isRecordingInProgressRef.current = isRecordingInProgress;
  }, [isRecordingInProgress])

  const [currentQuestionNumber, setCurrentQuestionNumber] = useState(0)

  const currentQuestion = questions[currentQuestionNumber] || {}

  const { 
    type: currentQuestionType,
    isOptional,
    soefiaConfig: {
      prompts = []
    } = {}
    // answerOptions: currentQuestionAnswerOptions = []
  } = currentQuestion;

  // --- Start question substep logic setup for soefia question type

  const isSoefiaQ = currentQuestionType === 'soefia'

  const totalQuestionSteps = isSoefiaQ ? prompts.length : 0;

  const [currentQuestionStep, setCurrentQuestionStep] = useState(0)

  const prevQuestionNumber = useRef(0)

  useEffect(() => {
    // We determine if the previous question is a higher number then the current question, then we came from the back button
    // and we set the step to the max number otherwise set it to 0
    if (prevQuestionNumber.current > currentQuestionNumber) {
      setCurrentQuestionStep(totalQuestionSteps)
    } else {
      setCurrentQuestionStep(0)
    }
    prevQuestionNumber.current = currentQuestionNumber;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestionNumber])

  // --- End question substep logic setup for soefia question type

  const [answers, setAnswers] = useState({})

  const [answersValidationMarks, setAnswersValidationMarks] = useState()

  // Need to solve closure issues when timer ends a recording early
  const answersRef = useRef({})
  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      hasUnsavedChanges.current = true;
    }
    answersRef.current = answers;
  }, [answers])


  const isPrevDisabled = (hasIntro ? false : currentQuestionNumber === 0) || isRecordingInProgress;

  // Only the teacher pulse check places students; any other assessment ignores the url param
  const requiredStudentTotal = assessmentName === 'TeacherPulseCheck' ? parseNumberOfStudents(numberOfStudents) : undefined

  const currentQuestionHasBeenAnswered = hasQuestionBeenAnswered(currentQuestion, answers, currentQuestionStep, requiredStudentTotal)

  const isNextDisabled = (
    (showTeamSelectorPage && teamColor) ? false 
    : isOptional ? false 
    : currentQuestionType === 'fileupload' ? false 
    // : isSoefiaQ ? false 
    : !currentQuestionHasBeenAnswered
  ) || isRecordingInProgress;

  const isLastQuestion = currentQuestionNumber === (questions.length - 1);

  const onPrevQuestion = () => {
    if (!isPrevDisabled) {
      if (isSoefiaQ && (currentQuestionStep > 0)) {
        setCurrentQuestionStep(currentQuestionStep - 1)
      } else if (hasIntro && currentQuestionNumber === 0) {
        if (hasTeamSelector && !showTeamSelectorPage) {
          setShowTeamSelectorPage(true)
        } else {
          setShowIntroPage(true)
        }
      } else {
        setCurrentQuestionNumber(currentQuestionNumber - 1);
      }
    }
  }

  const onNextQuestion = (event, forceNext) => {
    if (showTeamSelectorPage) {
      setShowTeamSelectorPage(false)
    } else if (isSoefiaQ && !isNextDisabled && (currentQuestionStep < totalQuestionSteps)) { 
      setCurrentQuestionStep(currentQuestionStep + 1)
     } else if ((forceNext && !isLastQuestion) || (!isNextDisabled && !isLastQuestion)) {
      setCurrentQuestionNumber(currentQuestionNumber + 1);
    }
  }

  const peformAutoNextAction = () => {
    setTimeout(() => {
      onNextQuestion(undefined, true);
    }, 300)
  }

  const onSubmitAnswerFileUpload = async (submissionResponse = {}, questionId, files = []) => {
    const { _id: assessmentAnswerId } = submissionResponse;
    const formData = new FormData();
    try {
      files.forEach((fileObj) => {
        const { file } = fileObj;
        formData.append('files', file);
      });
      formData.append('assessmentAnswerId', assessmentAnswerId);
      formData.append('questionId', questionId);
    } catch (e) { 
      console.log(e) 
    }

    const response = await fetch(`assessment/submit/file/upload`, 
    { 
      method: 'POST',
      body: formData
    })

    await response.json();
  }

  // May want to revisit this logic and look into a single bulk operation
  // Right now this calls an api for every single questionId and promptId combo so not as effecient as it can be
  // But works for now in terms of a POC (and could go into production, just could be made more effectient)
  const onSubmitSoefiaFileUpload = async (submissionResponse = {}, questionId = '', promptId = '', files = []) => {
    const { _id: assessmentAnswerId = '' } = submissionResponse;
    const formData = new FormData();
    try {
      files.forEach((fileObj) => {
        const { file } = fileObj;
        formData.append('files', file);
      });
      formData.append('assessmentAnswerId', assessmentAnswerId);
      formData.append('questionId', questionId);
      formData.append('promptId', promptId);
    } catch (e) { 
      console.log(e) 
    }

    const response = await fetch(`assessment/submit/soefia/file/upload`, 
    { 
      method: 'POST',
      body: formData
    })

    await response.json();
  }

  // Process potential files to upload into a question id map
  const getFilesObj = (answerSubmissionObj) => {
    const { answers } = answerSubmissionObj;
    const filesObj = {};
    Object.keys(answers).forEach(answerKey => {
      const answer = answers[answerKey];
      if (answer && answer.files && answer.files.length > 0) {
        filesObj[answerKey] = answer.files;
      }
    });
    return filesObj;
  }

  const getSoefiaFilesObj = (answerSubmissionObj) => {
    const { answers } = answerSubmissionObj;
    const filesObj = {};
    Object.keys(answers).forEach(answerKey => {
      const answer = answers[answerKey];

      const { soefiaAnswer = {} } = answer;

      Object.keys(soefiaAnswer).forEach(soefiaPromptAnswerKey => {
        const soefiaPromptAnswer = soefiaAnswer[soefiaPromptAnswerKey];

        if (soefiaPromptAnswer && soefiaPromptAnswer.files && soefiaPromptAnswer.files.length > 0) {
          filesObj[`${answerKey}-${soefiaPromptAnswerKey}`] = soefiaPromptAnswer.files;
        }
      });
    });
    return filesObj;
  }

  const checkAndExecuteFileUploads = async (answerSubmissionObj = {}, submissionResponse) => {
    const soefiaUploads = [];
    // Check standard file uploads
    const filesObj = getFilesObj(answerSubmissionObj) || {}
    const filesObjKeys = Object.keys(filesObj);
    if (filesObjKeys.length > 0) {
      for(let i = 0; i < filesObjKeys.length; i++) {
        const fileObjKey = filesObjKeys[i];
        const files = filesObj[fileObjKey]

        await onSubmitAnswerFileUpload(submissionResponse, fileObjKey, files)
      }
    }

    // Check for soefia audio file uploads
    const soefiaFilesObj = getSoefiaFilesObj(answerSubmissionObj) || {}
    const soefiaFilesObjKeys = Object.keys(soefiaFilesObj);
    if (soefiaFilesObjKeys.length > 0) {
      for(let i = 0; i < soefiaFilesObjKeys.length; i++) {
        const soefiaFilesObjKey = soefiaFilesObjKeys[i];

        const [questionId, promptId] = soefiaFilesObjKey.split('-')

        soefiaUploads.push(questionId)

        const files = soefiaFilesObj[soefiaFilesObjKey]

        await onSubmitSoefiaFileUpload(submissionResponse, questionId, promptId, files)
      }
    }

    return soefiaUploads;
  }

  // const generateSoefiaMetrics = async (assessmentAnswersObj = {}, questionId) => {
  //   const { _id: assessmentAnswerId = '' } = assessmentAnswersObj;
  //   const response = await fetch(`assessment/soefia/generateMetrics`, 
  //   { 
  //     method: 'POST',
  //     headers: {
  //       "Content-Type": "application/json",
  //     },
  //     body: JSON.stringify({ assessmentAnswerId, questionId })
  //   })

  //   await response.json();
  // }

  const validateAllQuestions = (answerToSubmit) => {
    const questionValidationMap = {}
    questions.forEach(question => {
      const { 
        id: questionId,
        type: currentQuestionType,
        isOptional
      } = question

      // force send 0 as last params since only relevant to soefia qs which are unused for vertical mode
      const qAnswered = hasQuestionBeenAnswered(question, answerToSubmit, 0, requiredStudentTotal)

      // marked so that we show true when an answer has not passed validation
      const hasQFailedValidation = (
        isOptional ? false 
        : currentQuestionType === 'fileupload' ? false 
        : !qAnswered
      )

      questionValidationMap[questionId] = hasQFailedValidation
    });

    // when we determine there are no items that failed a validation (with a val of true), we mark allPass as true
    const allPass = Object.values(questionValidationMap).every(val => val === false)

    if (allPass) {
      setAnswersValidationMarks(undefined)
    } else {
      setAnswersValidationMarks(questionValidationMap)
    }

    return allPass
  }

  const onSubmitAnswers = async (answersOverride) => {
    let allPass = true
    if (isVerticalScrollMode) {
      allPass = validateAllQuestions(answers)
    }
    if (allPass) {
      if (!isPreview && !isPractice) {
        try {
          setLoading(true)

          const answerToSubmit = answersOverride || answers

          const answerSubmissionObj = {
            assessmentId,
            assessmentName,
            answers: answerToSubmit
          }

          if (userId) {
            answerSubmissionObj.userId = userId;
          }

          if (classId) {
            answerSubmissionObj.classId = classId;
          }

          if (episodeId) {
            answerSubmissionObj.episodeId = episodeId;
          }

          if (eventId) {
            answerSubmissionObj.eventId = eventId;
          }

          if (teamColor) {
            answerSubmissionObj.teamColor = teamColor;
          }

          if (teamName) {
            answerSubmissionObj.teamName = teamName;
          }

          if (assignmentId) {
            answerSubmissionObj.assignmentId = assignmentId;
          }

          if (assignmentTargetType) {
            answerSubmissionObj.assignmentTargetType = assignmentTargetType;
          }

          if (groupId) {
            answerSubmissionObj.groupId = groupId;
          }

          if (sessionId) {
            answerSubmissionObj.sessionId = sessionId;
          }

          if (isSoefiaAssessment) {
            answerSubmissionObj.needsSoefiaMetricsGeneration = true;
          }

          if (modName) {
            answerSubmissionObj.modName = modName;
          }

          const response = await fetch(`assessment/submit`, 
          { 
            method: 'POST',
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ data: answerSubmissionObj, postSurveyQFCAction })
          })

          const jsonData = await response.json();

          if (jsonData.success) {
            await checkAndExecuteFileUploads(answerSubmissionObj, jsonData)

            // const soefiaQuestionUploadsRaw = await checkAndExecuteFileUploads(answerSubmissionObj, jsonData)
            // const soefiaQuestionUploads = [...new Set(soefiaQuestionUploadsRaw)] // Remove duplicate question ids from the array

            // if (soefiaQuestionUploads.length > 0) {
            //   // for(let i = 0; i < soefiaQuestionUploads.length; i++) {
            //   //   const soefiaQuestionId = soefiaQuestionUploads[i];
            //   //  await generateSoefiaMetrics(jsonData, soefiaQuestionId)
            //   // }
            //   await generateSoefiaMetrics(jsonData)
            // }

            const onSuccessSoefiaData = isSoefiaAssessment ? { ...jsonData, isSoefiaAssessment } : undefined;

            onSuccess(onSuccessSoefiaData, jsonData.followupAssessmentName, jsonData.followupAssessmentMod);
          }

          // If we have a followUp chain (url-based or api-branched)...we want to keep loader spinning to allow for a smoother transition
          if (!hasFollowUp && !jsonData.followupAssessmentName) {
            setLoading(false)
          }
        } catch (e) {
          setLoading(false)
        }
      } else {
        if (isPractice) {
          onSuccess();
        } else {
          onClose();
        }
      }
      hasUnsavedChanges.current = false;
    }
  }

  const [timesUpBlockingScreen, setTimesUpBlockingScreen] = useState(false)

  const onTimesUp = () => {
    if (isRecordingInProgressRef.current) {
      setTimesUpBlockingScreen(true)
      window.dispatchEvent(new CustomEvent("timesUp", {}));
      setTimeout(() => {
        onSubmitAnswers(answersRef.current)
        setTimesUpBlockingScreen(false)
      }, 2500)
    } else {
      onSubmitAnswers()
    }
  }

  const completedPercentage = (currentQuestionNumber / questions.length) * 100;
  const progressPercentage = isLastQuestion && currentQuestionHasBeenAnswered ? 100 : completedPercentage;

  const wrapperStyle = {
    position: 'relative', 
    height: 'calc(100vh)', 
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  }

  if (isSoefiaAssessment && !backgroundImage) {
    // wrapperStyle.background = '#3c546e'
    wrapperStyle.backgroundImage = `url(${SoefiaBackground})`;
    wrapperStyle.backgroundSize = 'cover';
    wrapperStyle.backgroundPosition = 'center center';
    wrapperStyle.fontFamily = '"Inter_24pt-Bold", "Open Sans", sans-serif';
  } else if (backgroundImage) {
    wrapperStyle.backgroundImage = `url(${backgroundImage})`;
    wrapperStyle.backgroundSize = 'cover';
    wrapperStyle.backgroundPosition = backgroundImageOrientation === 'topLeft' ? 'top left' : 'center center';
  } else {
    wrapperStyle.background = '#1282B2';
  }

  return (
    <div style={wrapperStyle}>
      { timesUpBlockingScreen && <FullScreenLoadingCover /> }
      {
        Boolean(timeLimit) &&
        <Timer timeLimit={timeLimit} onTimesUp={onTimesUp} />
      }
      {
        hasCloseButton &&
        <div 
          style={{
            cursor: 'pointer',
            position: 'absolute',
            top: '12px',
            right: '5px',
            background: '#7E7E7E',
            padding: '8px',
            borderRadius: '50%'
          }}
          onClick={onClose}
        >
          <div style={{ 
            color: '#FFFFFF',
            fontSize: '22px',
            width: '22px',
            height: '22px',
          }}>
            <FaTimes />
          </div>
        </div>
      }
      {
        loading ?
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: backgroundImage ? '#00000080' : 'inherit',
          fontSize: '16px',
          color: '#d2275a',
          overflow: 'auto',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <LoadingIcon />
        </div>
        :
        (assessmentDisabled || assessmentNotFound) ?
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: 'inherit',
          color: '#ffffff',
          fontWeight: 'bold',
          fontSize: '32px',
          textAlign: 'center',
          overflow: 'auto',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div>{assessmentDisabled ? 'Survey no longer active' : 'Survey not found'}</div>
        </div>
        :
        showIntroPage ?
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: backgroundImage ? '#00000080' : 'inherit',
          fontSize: '16px',
          color: isSoefiaQ ? '#c7ffff' : '#ffffff',
          overflow: 'auto',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{ 
            fontSize: '48px', 
            textAlign: 'center',
            fontFamily: isSoefiaAssessment ? '"PassionOne-Regular", "Open Sans", sans-serif' : 'inherit'
          }}>
            {title}
          </div>
          <div style={{ margin: '30px 0px', fontSize: '22px', whiteSpace: 'pre-wrap', width: '80%', textAlign: 'center' }}>
            {subTitle}
          </div>
          <div 
            style={{
              padding: '15px 20px',
              background: isSoefiaAssessment ? '#f28a22' : '#d2275a',
              border: '1px solid 7E7E7E',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
            onClick={() => {
              if (hasTeamSelector) {
                setShowTeamSelectorPage(true)
              } 
              setShowIntroPage(false)
            }}
          >
            {buttonText}
          </div>
        </div>
        :
      <>
      {
        isVerticalScrollMode ? <></> :
        !isSoefiaAssessment &&
        <ProgressBar percentage={progressPercentage} />
      }
      {
        isVerticalScrollMode ? <></> :
        isSoefiaAssessment ? 
        <QuestChevron direction='left' onClick={onPrevQuestion} isDisabled={isPrevDisabled} /> :
        <Chevron direction='left' onClick={onPrevQuestion} isDisabled={isPrevDisabled} />
      }
      {
        isVerticalScrollMode ? <></> :
        isLastQuestion && (
          isOptional ? true 
          : currentQuestionType === 'fileupload' ? true 
          : isSoefiaQ ? ( currentQuestionStep === totalQuestionSteps && currentQuestionHasBeenAnswered )
          : currentQuestionHasBeenAnswered
        )
        ?
        <div 
          style={{
            position: 'absolute',
            marginTop: '10px',
            // top: '50%',
            top: isMobileView ? 'auto' : '50%',
            bottom: isMobileView ? '20px' : 'auto',
            // right: '15px',
            right: isMobileView ? '7px' : '15px',
            padding: '15px 25px',
            borderRadius: '15px',
            background: isSoefiaAssessment ? '#f28a22' : '#d2275a',
            color: '#ffffff',
            cursor: 'pointer',
            fontSize: '22px'
          }}
          onClick={() => onSubmitAnswers()}
        >
          {'Submit'}
        </div>
        :
        isSoefiaAssessment ?
        <QuestChevron direction='right' onClick={onNextQuestion} isDisabled={isNextDisabled || (!isSoefiaQ && isLastQuestion)} />
        :
        <Chevron direction='right' onClick={onNextQuestion} isDisabled={isNextDisabled || (!isSoefiaQ && isLastQuestion)} />
      }
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        background: backgroundImage ? '#00000080' : 'inherit',
        fontSize: '16px',
        color: isSoefiaQ ? '#c7ffff' : '#ffffff',
        overflow: 'auto'
      }}>
        { showTeamSelectorPage ?
          <TeamSelector 
            teamSelectorType={teamSelector} 
            teamColor={teamColor} 
            setTeamColor={setTeamColor}
            peformAutoNextAction={peformAutoNextAction}
          />
          :
          (
            isVerticalScrollMode ?
            <div style={{
              marginTop: '40px',
              marginBottom: '40px'
            }}>
              <div>
              {
                questions.map(currentQuestionObj => (
                  <MainQuestionAnswerPanel
                    key={currentQuestionObj.id}
                    questionObj={currentQuestionObj}
                    categoryInfoMap={categoryInfoMap}
                    answers={answers}
                    setAnswers={setAnswers}
                    largeFonts={largeFonts}
                    extraLargeFonts={extraLargeFonts}
                    reverseOptions={reverseOptions}
                    isVerticalScrollMode={isVerticalScrollMode}
                    answersValidationMarks={answersValidationMarks}
                    requiredStudentTotal={requiredStudentTotal}
                  />
                ))
              }
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                paddingTop: '25px'
              }}>
                <div 
                  style={{
                    marginTop: '10px',
                    padding: '15px 25px',
                    borderRadius: '15px',
                    background: isSoefiaAssessment ? '#f28a22' : '#d2275a',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '22px'
                  }}
                  onClick={() => onSubmitAnswers()}
                >
                  {'Submit'}
                </div>
              </div>
            </div>
            :
            <MainQuestionAnswerPanel
              key={currentQuestion.id}
              questionObj={currentQuestion}
              currentQuestionStep={currentQuestionStep}
              categoryInfoMap={categoryInfoMap}
              answers={answers}
              setAnswers={setAnswers}
              peformAutoNextAction={peformAutoNextAction}
              largeFonts={largeFonts}
              extraLargeFonts={extraLargeFonts}
              reverseOptions={reverseOptions}
              autoPlayAudio={autoPlayAudio}
              setIsRecordingInProgress={setIsRecordingInProgress}
              requiredStudentTotal={requiredStudentTotal}
            />
          )
        }
      </div>
      </>
      }
    </div>
  )
}